import type { V10ControlRoomSection, V10ProjectMode } from "./v10-phase-model.js";
import type { V10QueueRiskLevel } from "./v10-queue-risk.js";

export interface V10QueueCard {
  readonly id: string;
  readonly clientName: string;
  readonly happened: string;
  readonly blocked: string;
  readonly ownerLabel: string;
  readonly riskLevel: V10QueueRiskLevel;
  readonly ageLabel: string;
  readonly ctaLabel: string;
  readonly projectId: string;
  readonly targetSection: V10ControlRoomSection;
  readonly mode: V10ProjectMode;
}

export interface V10ProjectListItem {
  readonly id: string;
  readonly name: string;
  readonly summary: string;
  readonly pills: readonly string[];
  readonly ageLabel: string;
  readonly group:
    | "draft"
    | "active"
    | "waiting-client"
    | "blocked"
    | "handoff-ready"
    | "live-care";
  readonly currentSection: V10ControlRoomSection;
  readonly mode: V10ProjectMode;
  readonly ownerLabel: string;
  readonly riskLevel: V10QueueRiskLevel;
  readonly dueLabel: string;
  readonly outstandingClientAction: boolean;
}

export interface V10InboxRow {
  readonly id: string;
  readonly clientName: string;
  readonly projectName: string;
  readonly projectId: string;
  readonly requestType:
    | "client-request"
    | "change-request"
    | "missing-info"
    | "approval-pending"
    | "safe-edit"
    | "handoff-question"
    | "live-care";
  readonly requestLabel: string;
  readonly status: string;
  readonly ageLabel: string;
  readonly ctaLabel: string;
  readonly targetSection: V10ControlRoomSection;
  readonly mode: V10ProjectMode;
}

export interface V10ScanRow {
  readonly id: string;
  readonly url: string;
  readonly label: string;
  readonly ageLabel: string;
  readonly projectId: string | null;
  readonly status: string;
}

export const V10_PROJECT_GROUP_LABELS: Record<V10ProjectListItem["group"], string> = {
  draft: "Draft / Pre-projects",
  active: "Active projects",
  "waiting-client": "Waiting client",
  blocked: "Blocked",
  "handoff-ready": "Handoff ready",
  "live-care": "Post-handoff care",
};

export const V10_FIXTURE_CONTINUE: readonly V10QueueCard[] = [
  {
    id: "cont-acme",
    clientName: "Acme HVAC",
    happened: "You shared the Client Link yesterday.",
    blocked: "Waiting for client review.",
    ownerLabel: "Jordan Owner",
    riskLevel: "medium",
    ageLabel: "1d ago",
    ctaLabel: "Continue",
    projectId: "acme",
    targetSection: "website",
    mode: "DEFAULT_GENERATION",
  },
  {
    id: "cont-metro",
    clientName: "Metro Roofing",
    happened: "Client submitted a change request.",
    blocked: "Agency review needed.",
    ownerLabel: "Agency owner",
    riskLevel: "medium",
    ageLabel: "4h ago",
    ctaLabel: "Open",
    projectId: "metro",
    targetSection: "changes",
    mode: "DEFAULT_GENERATION",
  },
];

export const V10_FIXTURE_ATTENTION: readonly V10QueueCard[] = [
  {
    id: "att-acme",
    clientName: "Acme HVAC",
    happened: "Preview is ready.",
    blocked: "Client Link not shared yet.",
    ownerLabel: "Jordan Owner",
    riskLevel: "low",
    ageLabel: "Today",
    ctaLabel: "Share link",
    projectId: "acme",
    targetSection: "website",
    mode: "DEFAULT_GENERATION",
  },
  {
    id: "att-northside",
    clientName: "Northside Dental",
    happened: "Scan found content blockers.",
    blocked: "Fix Kit review needed.",
    ownerLabel: "Agency owner",
    riskLevel: "high",
    ageLabel: "2d ago",
    ctaLabel: "Review preview",
    projectId: "northside",
    targetSection: "business",
    mode: "EXISTING_SITE_SCAN",
  },
];

export const V10_FIXTURE_CLIENT_REQUESTS: readonly V10QueueCard[] = [
  {
    id: "req-metro",
    clientName: "Metro Roofing",
    happened: "Client asked for headline change.",
    blocked: "Safe edit queue waiting.",
    ownerLabel: "Agency owner",
    riskLevel: "medium",
    ageLabel: "4h ago",
    ctaLabel: "Review request",
    projectId: "metro",
    targetSection: "changes",
    mode: "DEFAULT_GENERATION",
  },
  {
    id: "req-acme",
    clientName: "Acme HVAC",
    happened: "Client opened the review room.",
    blocked: "Approval pending.",
    ownerLabel: "Jordan Owner",
    riskLevel: "medium",
    ageLabel: "1h ago",
    ctaLabel: "Review request",
    projectId: "acme",
    targetSection: "approval-delivery",
    mode: "DEFAULT_GENERATION",
  },
];

export const V10_FIXTURE_STUCK: readonly V10QueueCard[] = [
  {
    id: "stuck-northside",
    clientName: "Northside Dental",
    happened: "Readiness check failed.",
    blocked: "Business facts need confirmation.",
    ownerLabel: "Agency owner",
    riskLevel: "high",
    ageLabel: "2d ago",
    ctaLabel: "Resolve blocker",
    projectId: "northside",
    targetSection: "business",
    mode: "EXISTING_SITE_SCAN",
  },
];

export const V10_FIXTURE_READY: readonly V10QueueCard[] = [
  {
    id: "ready-bright",
    clientName: "Bright Smile",
    happened: "Departure Kit packaged.",
    blocked: "Ready for owner download.",
    ownerLabel: "Agency owner",
    riskLevel: "low",
    ageLabel: "Today",
    ctaLabel: "Finish handoff",
    projectId: "bright",
    targetSection: "approval-delivery",
    mode: "DEFAULT_GENERATION",
  },
  {
    id: "ready-sunrise",
    clientName: "Sunrise Plumbing",
    happened: "Handoff complete.",
    blocked: "Post-handoff care is display-only.",
    ownerLabel: "Agency owner",
    riskLevel: "low",
    ageLabel: "3d ago",
    ctaLabel: "Open care note",
    projectId: "sunrise",
    targetSection: "overview",
    mode: "DEFAULT_GENERATION",
  },
];

export const V10_FIXTURE_PORTFOLIO_SIGNALS = [
  { value: "12", label: "Active projects" },
  { value: "4", label: "Ready for client review" },
  { value: "3", label: "Need fixing" },
] as const;

export const V10_FIXTURE_PROJECTS: readonly V10ProjectListItem[] = [
  {
    id: "draft-1",
    name: "Lakeside Auto",
    summary: "Draft started from agency facts.",
    pills: ["Draft"],
    ageLabel: "Today",
    group: "draft",
    currentSection: "overview",
    mode: "DEFAULT_GENERATION",
    ownerLabel: "Agency owner",
    riskLevel: "low",
    dueLabel: "Setup in progress",
    outstandingClientAction: false,
  },
  {
    id: "acme",
    name: "Acme HVAC",
    summary: "Preview ready. Client Link can be shared.",
    pills: ["Ready for review"],
    ageLabel: "1d ago",
    group: "active",
    currentSection: "website",
    mode: "DEFAULT_GENERATION",
    ownerLabel: "Jordan Owner",
    riskLevel: "medium",
    dueLabel: "On track",
    outstandingClientAction: true,
  },
  {
    id: "acme-wait",
    name: "Acme HVAC",
    summary: "Waiting on client intake submission.",
    pills: ["Waiting client"],
    ageLabel: "3h ago",
    group: "waiting-client",
    currentSection: "overview",
    mode: "DEFAULT_GENERATION",
    ownerLabel: "Jordan Owner",
    riskLevel: "medium",
    dueLabel: "Waiting on client",
    outstandingClientAction: true,
  },
  {
    id: "northside",
    name: "Northside Dental",
    summary: "Proof check found business-content blockers.",
    pills: ["Blocked", "Fix Kit"],
    ageLabel: "2d ago",
    group: "blocked",
    currentSection: "business",
    mode: "EXISTING_SITE_SCAN",
    ownerLabel: "Agency owner",
    riskLevel: "high",
    dueLabel: "Blocked / overdue",
    outstandingClientAction: false,
  },
  {
    id: "metro",
    name: "Metro Roofing",
    summary: "Change request submitted from Client Link.",
    pills: ["Client change"],
    ageLabel: "4h ago",
    group: "active",
    currentSection: "changes",
    mode: "DEFAULT_GENERATION",
    ownerLabel: "Sam Lead",
    riskLevel: "medium",
    dueLabel: "On track",
    outstandingClientAction: true,
  },
  {
    id: "bright",
    name: "Bright Smile",
    summary: "Handoff package is ready for owner download.",
    pills: ["Handoff ready"],
    ageLabel: "Today",
    group: "handoff-ready",
    currentSection: "approval-delivery",
    mode: "DEFAULT_GENERATION",
    ownerLabel: "Agency owner",
    riskLevel: "low",
    dueLabel: "Handoff due",
    outstandingClientAction: false,
  },
  {
    id: "sunrise",
    name: "Sunrise Plumbing",
    summary: "Post-handoff care note and safe edit window.",
    pills: ["Post-handoff"],
    ageLabel: "3d ago",
    group: "live-care",
    currentSection: "overview",
    mode: "DEFAULT_GENERATION",
    ownerLabel: "Agency owner",
    riskLevel: "low",
    dueLabel: "On track",
    outstandingClientAction: false,
  },
];

export const V10_FIXTURE_INBOX: readonly V10InboxRow[] = [
  {
    id: "in-metro",
    clientName: "Metro Roofing",
    projectName: "Metro Roofing",
    projectId: "metro",
    requestType: "change-request",
    requestLabel: "Change request",
    status: "Pending agency review",
    ageLabel: "4h ago",
    ctaLabel: "Review changes",
    targetSection: "changes",
    mode: "DEFAULT_GENERATION",
  },
  {
    id: "in-acme",
    clientName: "Acme HVAC",
    projectName: "Acme HVAC",
    projectId: "acme",
    requestType: "approval-pending",
    requestLabel: "Approval pending",
    status: "Client opened review room",
    ageLabel: "1h ago",
    ctaLabel: "Review approval",
    targetSection: "approval-delivery",
    mode: "DEFAULT_GENERATION",
  },
  {
    id: "in-draft",
    clientName: "Lakeside Auto",
    projectName: "Lakeside Auto",
    projectId: "draft-1",
    requestType: "missing-info",
    requestLabel: "Missing info",
    status: "Intake incomplete",
    ageLabel: "Today",
    ctaLabel: "Complete setup",
    targetSection: "overview",
    mode: "DEFAULT_GENERATION",
  },
  {
    id: "in-bright",
    clientName: "Bright Smile",
    projectName: "Bright Smile",
    projectId: "bright",
    requestType: "handoff-question",
    requestLabel: "Handoff question",
    status: "Client asked about ownership record",
    ageLabel: "Today",
    ctaLabel: "Answer handoff",
    targetSection: "approval-delivery",
    mode: "DEFAULT_GENERATION",
  },
  {
    id: "in-sunrise",
    clientName: "Sunrise Plumbing",
    projectName: "Sunrise Plumbing",
    projectId: "sunrise",
    requestType: "live-care",
    requestLabel: "Post-handoff care question",
    status: "Safe edit question",
    ageLabel: "2d ago",
    ctaLabel: "Open care note",
    targetSection: "overview",
    mode: "DEFAULT_GENERATION",
  },
];

export const V10_FIXTURE_SCANS: readonly V10ScanRow[] = [
  {
    id: "scan-1",
    url: "https://northside-dental.example",
    label: "Northside Dental",
    ageLabel: "2d ago",
    projectId: "northside",
    status: "Blockers found",
  },
  {
    id: "scan-2",
    url: "https://metro-roofing.example",
    label: "Metro Roofing",
    ageLabel: "1w ago",
    projectId: "metro",
    status: "Relaunch opportunity",
  },
];

export function v10FixtureProject(projectId: string): V10ProjectListItem | undefined {
  return V10_FIXTURE_PROJECTS.find((p) => p.id === projectId);
}

export const V10_FIXTURE_AGENCY = {
  name: "Demo Agency",
  deployEnv: "local",
} as const;

export const V10_NEW_PROJECT_ID = "draft-1";
